import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import GetGatheringForFullPublicPhotosDocument from "src/api/graphql/__generated__/documents/GetGatheringForFullPublicPhotosDocument";
import { FullPublicPhotoFragmentFragment } from "src/api/graphql/__generated__/graphql";
import useDevice from "src/hooks/useDevice";
import useStateRef from "src/hooks/useStateRef";
import useSyncRef from "src/hooks/useSyncRef";
import useGatheringPublicFullContext from "../../../hooks/useGatheringPublicFullContext";

const useGatheringFullPublicPhotos = (gatheringId: string) => {
	const { device } = useDevice();
	const { data, previousData, fetchMore, refetch } = useQuery(
		GetGatheringForFullPublicPhotosDocument,
		{
			variables: {
				input: {
					gatheringId,
				},
				photosInput: {
					limit: device === "desktop" ? 32 : 20,
				},
			},
			fetchPolicy: "cache-and-network",
		},
	);
	const gathering = data?.getGathering ?? previousData?.getGathering;
	const cursorRef = useSyncRef(gathering?.photos?.cursor ?? null);
	const { store } = useGatheringPublicFullContext();

	const [loading, setLoading, loadingRef] = useStateRef(false);
	const [refreshing, setRefreshing, refreshingRef] = useStateRef(false);
	const handleFetchMore = useCallback(async () => {
		if (!cursorRef.current) return;
		if (loadingRef.current) return;
		if (refreshingRef.current) return;
		if (cursorRef.current === "{}") return;
		setLoading(true);
		try {
			const fetchPromise = fetchMore({
				variables: {
					input: {
						gatheringId,
					},
					photosInput: {
						cursor: cursorRef.current,
						limit: 32,
					},
				},
			});
			const { didFilterScrollPopup, setDidFilterScrollPopup, setPopUpVariant } =
				store.getState();
			if (!didFilterScrollPopup) {
				setDidFilterScrollPopup(true);
				setPopUpVariant("FilterPhotos");
			}

			await fetchPromise;
		} finally {
			setLoading(false);
		}
	}, [cursorRef, fetchMore, gatheringId, loadingRef, refreshingRef, setLoading, store]);

	const handleRefresh = useCallback(async () => {
		if (loadingRef.current) return;
		if (refreshingRef.current) return;
		setRefreshing(true);
		try {
			await refetch({
				input: {
					gatheringId,
				},
			});
		} finally {
			setRefreshing(false);
		}
	}, [gatheringId, loadingRef, refreshingRef, refetch, setRefreshing]);

	const photos = useMemo<FullPublicPhotoFragmentFragment[]>(() => {
		return gathering?.photos?.payload ?? [];
	}, [gathering?.photos?.payload]);

	return {
		gathering,
		photos,
		loading,
		refreshing,
		fetchMore: handleFetchMore,
		refresh: handleRefresh,
	};
};
export default useGatheringFullPublicPhotos;
